import React, { useEffect, useState } from 'react';
import { Control, Controller, useFormContext } from 'react-hook-form';
import { Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { RRule } from 'rrule';
import dayjs from 'dayjs';
import CustomRecurrence from './CustomRecurrence';
import { getWeekday, maxDate, recurrenceOptions, today } from './RecurrenceService';

interface RecurrenceSectionProps {
  control: Control<any>;
  disabled?: boolean;
}

type RecurrenceOption = {
  value: string;
  label: string;
};

const RecurrenceSection: React.FC<RecurrenceSectionProps> = ({ control, disabled = false }) => {
  const { setValue, watch } = useFormContext();
  const [isCustom, setIsCustom] = useState(false);
  const [simpleSelection, setSimpleSelection] = useState('none');

  const options: RecurrenceOption[] = recurrenceOptions();

  const startTime = watch('startTime');

  const handleSimpleSelectionChange = (value: string) => {
    setSimpleSelection(value);
    setIsCustom(value === 'custom');

    let rrule = '';
    const baseOptions = {
      interval: 1,
      until: dayjs(maxDate()).toDate(),
      dtstart: startTime ? new Date(startTime) : new Date(dayjs(today()).toISOString()),
    };
    switch (value) {
      case 'none':
        rrule = '';
        break;
      case 'daily':
        rrule = new RRule({ ...baseOptions, freq: RRule.DAILY }).toString();
        break;
      case 'weekly':
        rrule = new RRule({
          ...baseOptions,
          freq: RRule.WEEKLY,
          byweekday: [getWeekday()],
        }).toString();
        break;
      case 'monthly':
        rrule = new RRule({
          ...baseOptions,
          freq: RRule.MONTHLY,
          byweekday: [getWeekday().nth(1)],
        }).toString();
        break;
      case 'annual':
        rrule = new RRule({
          ...baseOptions,
          freq: RRule.YEARLY,
          bymonth: [dayjs().month() + 1],
          bymonthday: [1],
        }).toString();
        break;
      case 'weekday':
        rrule = new RRule({
          ...baseOptions,
          freq: RRule.WEEKLY,
          byweekday: [RRule.MO, RRule.TU, RRule.WE, RRule.TH, RRule.FR],
        }).toString();
        break;
    }
    setValue('recurrenceRule', rrule);
  };

  useEffect(() => {
    if (startTime) {
      handleSimpleSelectionChange(simpleSelection);
    }
  }, [startTime]);

  return (
    <Box>
      <FormControl fullWidth>
        <InputLabel>Recurrence</InputLabel>
        <Controller
          name="recurrenceRule"
          control={control}
          render={() => {
            return (
              <Select
                fullWidth
                value={simpleSelection}
                disabled={disabled}
                onChange={e => {
                  const value = e.target.value as string;
                  handleSimpleSelectionChange(value);
                }}
              >
                {options.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            );
          }}
        />
      </FormControl>

      {isCustom && (
        <CustomRecurrence
          onChange={rrule => setValue('recurrenceRule', rrule)}
          startTime={startTime}
        />
      )}
    </Box>
  );
};

export default RecurrenceSection;
